import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * Listens to both:
 * - Theme `cart:update` (custom product-info ATC, cart-items, protection toggle)
 * - Shopify StandardEvents `cartLinesUpdate` (Horizon 4.1.3+ native product-form)
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {string | null} */
  #standardEventName = null;

  /** @type {number | null} */
  #syncTimer = null;

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount?.textContent ?? '0', 10) || 0;
  }

  set currentCartCount(value) {
    if (!this.refs.cartBubbleCount) return;
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onThemeCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.syncFromCartJs();
    this.#bindStandardEvents();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onThemeCartUpdate);
    if (this.#standardEventName) {
      document.removeEventListener(this.#standardEventName, this.onStandardCartUpdate);
    }
    window.removeEventListener('pageshow', this.onPageShow);
    if (this.#syncTimer) window.clearTimeout(this.#syncTimer);
  }

  async #bindStandardEvents() {
    try {
      const { StandardEvents } = await import('@shopify/events');
      this.#standardEventName = StandardEvents.cartLinesUpdate;
      document.addEventListener(this.#standardEventName, this.onStandardCartUpdate);
    } catch (_) {
      // Older drafts may omit @shopify/events — Theme cart:update still syncs the bubble.
    }
  }

  /**
   * @param {PageTransitionEvent} event
   */
  onPageShow = (event) => {
    if (event.persisted) this.syncFromCartJs();
  };

  /**
   * Horizon 4.1.3+ native cart lines update — absolute totalQuantity.
   * @param {{ promise?: Promise<{ cart?: { totalQuantity?: number }, detail?: { itemCount?: number } }> }} event
   */
  onStandardCartUpdate = (event) => {
    event.promise
      ?.then(({ cart, detail }) => {
        const itemCount = cart?.totalQuantity ?? detail?.itemCount;
        if (window.ssCartCount) {
          this.syncFromCartJs();
        } else if (typeof itemCount === 'number') {
          this.renderCartBubble(itemCount);
        } else {
          this.syncFromCartJs();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-icon] StandardEvents promise rejected:', error);
      });
  };

  /**
   * Theme cart:update — used by custom ATC, cart drawer, quantity changes.
   * @param {CustomEvent} event
   */
  onThemeCartUpdate = (event) => {
    const data = event.detail?.data || {};
    const resource = event.detail?.resource;
    const comingFromProductForm = data.source === 'product-form-component';

    /** @type {number | undefined} */
    let absolute;
    if (typeof resource?.items !== 'undefined' && window.ssCartCount) {
      absolute = window.ssCartCount.paidCount(resource);
    } else if (typeof resource?.item_count === 'number') {
      if (window.ssCartCount && resource?.items) {
        absolute = window.ssCartCount.paidCount(resource);
      } else {
        absolute = resource.item_count;
        this.scheduleSyncFromCartJs();
      }
    } else if (typeof resource?.totalQuantity === 'number') {
      this.scheduleSyncFromCartJs();
      return;
    } else     if (typeof data.itemCount === 'number') {
      // cart/add.js has no top-level item_count — custom ATC sends absolute itemCount;
      // legacy product-form-component sends the quantity just added (delta).
      absolute = comingFromProductForm ? this.currentCartCount + data.itemCount : data.itemCount;
    }

    if (typeof absolute === 'number' && Number.isFinite(absolute)) {
      this.renderCartBubble(absolute);
      if (absolute === 0) this.scheduleSyncFromCartJs();
      return;
    }

    // Missing count — don't assume 0 (that was wiping a correct badge).
    this.scheduleSyncFromCartJs();
  };

  scheduleSyncFromCartJs = () => {
    if (this.#syncTimer) window.clearTimeout(this.#syncTimer);
    this.#syncTimer = window.setTimeout(() => {
      this.#syncTimer = null;
      this.syncFromCartJs();
    }, 50);
  };

  syncFromCartJs = async () => {
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      const count = window.ssCartCount ? window.ssCartCount.paidCount(cart) : cart.item_count;
      this.renderCartBubble(typeof count === 'number' ? count : 0, false);
    } catch (_) {
      // no-op
    }
  };

  /**
   * @param {number} itemCount - Absolute cart item count.
   * @param {boolean} [animate=true]
   */
  renderCartBubble = async (itemCount, animate = true) => {
    const count = Math.max(0, Number(itemCount) || 0);
    const previous = this.currentCartCount;

    if (count === 0) sessionStorage.removeItem('cart-count');

    // Multiple cart:update events often fire with the same total (ATC + morph +
    // drawer sync). Re-running the pulse makes the badge "blink" 3 → 3.
    if (count === previous) return;

    this.currentCartCount = count;
    this.classList.toggle('header-actions__cart-icon--has-cart', count > 0);

    this.refs.cartBubbleCount?.classList.remove('hidden', 'visually-hidden');
    this.refs.cartBubble?.classList.remove('hidden', 'visually-hidden');

    if (count === 0) {
      sessionStorage.removeItem('cart-count');
    } else {
      sessionStorage.setItem(
        'cart-count',
        JSON.stringify({
          value: String(count),
          timestamp: Date.now(),
        })
      );
    }

    // Pulse only when the count increases (add-to-cart), not on qty decreases.
    if (!animate || count === 0 || count <= previous || !this.refs.cartBubble) return;

    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    if (this.refs.cartBubbleText) {
      await onAnimationEnd(this.refs.cartBubbleText);
    }
    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  ensureCartBubbleIsCorrect = () => {
    this.syncFromCartJs();
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
